// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 9e7660d2-297d-4c6e-98c7-82d09de5b91c

// ---------------------------------------------------------------------------
// Forms round-trip — closing the discovery loop for people who live in Excel.
//
// Not every application owner will log into a web form. Most of them want a
// spreadsheet in their inbox, filled in between meetings, and emailed back.
// This component is both ends of that loop:
//
//   1. GENERATE — build an .xlsx straight FROM the live schema (so the form can
//      never drift from the model), pre-filled with whatever we already know,
//      so the owner only has to complete the blanks.
//
//   2. INGEST — read the returned workbook, show a diff preview (old -> new,
//      per field, per application) and only write the rows the reviewer accepts.
//
// The trick that makes the round-trip reliable is the hidden "_meta" sheet.
// A normal spreadsheet round-trip has to guess: which row is which application,
// and which column is which field? Names get edited, columns get reordered, and
// fuzzy matching quietly writes the wrong value onto the wrong fact sheet. So
// the generated workbook carries its own identity map — one entry per data row
// (fact sheet id + name) and one per column (0-based index + field key + label).
// On ingest we resolve rows by id (matched by name first, index as a fallback,
// so inserted/deleted rows do not shift the mapping) and columns by field key.
// If someone rebuilds the file from scratch and the _meta sheet is gone, we
// degrade to name/label matching — exactly like ImportDialog does — and say so
// loudly in the UI rather than pretending the match is exact.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Stack, TextField, InputAdornment, Button,
  Checkbox, FormControlLabel, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, LinearProgress, Divider, List, ListItemButton, ListItemText,
  Tooltip, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import * as XLSX from 'xlsx';
import { allFieldsFor, resolvePicklist, FIELD_TYPES } from './schema';
import store from './store';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

const SHEET_README = 'Read me';
const SHEET_DATA = 'Applications';
const SHEET_VALUES = 'Valid values';
const SHEET_META = '_meta';

// An application is "incomplete" below this completion score — the usual case
// for "only send the form to the owners who still owe us data".
const INCOMPLETE_BELOW = 100;

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Same alias vocabulary ImportDialog uses, for the degraded (no _meta) path.
const ALIASES = {
  name: ['name', 'displayname', 'appname', 'applicationname', 'application'],
  description: ['description', 'appdescription'],
  hostingType: ['hostingtype', 'hosting'],
  locationType: ['currenthostinglocation', 'hostinglocation', 'location'],
  targetHosting: ['desiredfuturehosting', 'futurehosting', 'targethosting'],
  timeClassification: ['timeclassification', 'time'],
  sixRClassification: ['6rclassification', 'sixr', 'disposition'],
  cmDisposition: ['cmdisposition'],
  drTier: ['drtier', 'disasterrecoverytier', 'dr'],
  applicationType: ['applicationtype', 'type', 'class'],
  businessCriticality: ['businesscriticality', 'criticality'],
  applicationOwner: ['businessowner', 'applicationowner', 'owner'],
  technicalOwner: ['technicalowner', 'itowner'],
  vendorName: ['vendorname', 'vendor'],
  businessUnit: ['functionalbusinessunit', 'businessunit', 'department'],
  lifecycleState: ['lifecycle', 'lifecyclestate', 'status'],
  appPurpose: ['whattheapplicationdoes', 'purpose'],
};

const TRUE_WORDS = new Set(['yes', 'y', 'true', '1', 'x']);
const FALSE_WORDS = new Set(['no', 'n', 'false', '0']);

const NUMERIC_TYPES = [FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY, FIELD_TYPES.PERCENT];
const LIST_TYPES = [FIELD_TYPES.MULTISELECT, FIELD_TYPES.TAGS];

// Display form of a stored value — also what we write into the sheet.
function fmt(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    return v
      .map((x) => (x && typeof x === 'object' ? x.value || x.name || '' : x))
      .filter(Boolean)
      .join(', ');
  }
  return String(v);
}

// Sheet cell -> stored value, typed by the field definition.
function parseCell(raw, field) {
  const s = String(raw === undefined || raw === null ? '' : raw).trim();
  if (!s) return '';
  if (field.type === FIELD_TYPES.BOOLEAN) {
    const l = s.toLowerCase();
    if (TRUE_WORDS.has(l)) return true;
    if (FALSE_WORDS.has(l)) return false;
    return s;
  }
  if (NUMERIC_TYPES.indexOf(field.type) >= 0) {
    const n = Number(s.replace(/[$,%\s]/g, ''));
    return Number.isFinite(n) ? n : s;
  }
  if (LIST_TYPES.indexOf(field.type) >= 0) {
    return s.split(/[;,]/).map((x) => x.trim()).filter(Boolean);
  }
  return s;
}

// Allowed values we can advertise in the workbook / validate on the way back.
function allowedValues(field) {
  if (field.type === FIELD_TYPES.BOOLEAN) return ['Yes', 'No'];
  if (!field.picklist) return [];
  return resolvePicklist(field.picklist) || [];
}

function violatesPicklist(field, value) {
  const opts = allowedValues(field);
  if (!opts.length) return false;
  const vals = Array.isArray(value) ? value : [fmt(value)];
  return vals.some((v) => String(v).trim() && !opts.some((o) => norm(o) === norm(v)));
}

// ---------------------------------------------------------------------------
const ROW_CAP = 250;   // rendered rows; selection and export cover everything

export default function FormsRoundtrip({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // The columns we can offer: every non-relation, non-lifecycle Application
  // field, de-duplicated by key (hostingType/vendorName appear in more than one
  // section of the schema and must not become two columns of the same field).
  const fields = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const f of allFieldsFor('Application')) {
      if (f.type === FIELD_TYPES.RELATION || f.type === FIELD_TYPES.LIFECYCLE) continue;
      if (seen.has(f.key)) continue;
      seen.add(f.key);
      out.push(f);
    }
    return out;
  }, []);

  const fieldByKey = useMemo(() => {
    const m = new Map();
    fields.forEach((f) => m.set(f.key, f));
    return m;
  }, [fields]);

  const sections = useMemo(() => {
    const out = [];
    fields.forEach((f) => { if (out.indexOf(f.section) < 0) out.push(f.section); });
    return out;
  }, [fields]);

  const reload = useCallback(async () => {
    setLoading(true);
    const rows = await store.listFactSheets(tenantId, { type: 'Application' });
    setApps(rows);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
        Offline discovery forms
      </Typography>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        Generate a pre-filled Excel questionnaire from the live schema, email it to the
        application owner, then ingest their answers back onto the right fact sheets.
      </Typography>

      <Paper variant="outlined">
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #e5e9ef', '& .MuiTab-root': { fontWeight: 600 } }}
        >
          <Tab label="Generate form" />
          <Tab label="Ingest completed form" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 ? (
            <GenerateTab
              tenantId={tenantId}
              apps={apps}
              loading={loading}
              fields={fields}
              sections={sections}
            />
          ) : (
            <IngestTab
              tenantId={tenantId}
              apps={apps}
              fields={fields}
              fieldByKey={fieldByKey}
              onApplied={reload}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TAB 1 — Generate
// ---------------------------------------------------------------------------
function GenerateTab({ tenantId, apps, loading, fields, sections }) {
  const [search, setSearch] = useState('');
  const [selectedApps, setSelectedApps] = useState(() => new Set());
  const [selectedSections, setSelectedSections] = useState(() => new Set(sections.slice(0, 3)));
  const [note, setNote] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) => String(a.name).toLowerCase().includes(q));
  }, [apps, search]);

  // Only the visible slice mounts: one ListItemButton + Checkbox per app is
  // thousands of controls on a real tenant. "Select all" and every export still
  // operate on the full `visible` set — the cap bounds rendering, not scope.
  const shown = useMemo(() => visible.slice(0, ROW_CAP), [visible]);

  const toggleApp = (id) => {
    const next = new Set(selectedApps);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedApps(next);
  };
  const toggleSection = (s) => {
    const next = new Set(selectedSections);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSelectedSections(next);
  };

  const selectAll = () => setSelectedApps(new Set(visible.map((a) => a.id)));
  const selectIncomplete = () =>
    setSelectedApps(new Set(visible.filter((a) => (a.completion || 0) < INCOMPLETE_BELOW).map((a) => a.id)));
  const selectNone = () => setSelectedApps(new Set());

  // "Name" is always column A: it is the human-readable identity of the row and
  // what the fallback matcher keys on when _meta has been lost.
  const nameField = fields.find((f) => f.key === 'name') || { key: 'name', label: 'Name', type: FIELD_TYPES.TEXT };
  const bodyFields = fields.filter((f) => f.key !== 'name' && selectedSections.has(f.section));
  const columns = [nameField, ...bodyFields];

  const chosen = apps.filter((a) => selectedApps.has(a.id));
  const canGenerate = chosen.length > 0 && bodyFields.length > 0;

  const generate = () => {
    const wb = XLSX.utils.book_new();

    // --- Read me -----------------------------------------------------------
    const readme = [
      ['Primordia — Application discovery form'],
      [],
      ['Workspace', tenantId],
      ['Generated', new Date().toISOString().slice(0, 10)],
      ['Applications in this form', String(chosen.length)],
      ['Questions in this form', String(bodyFields.length)],
      [],
      ['How to fill this in'],
      ['1.', `Go to the "${SHEET_DATA}" sheet. Each ROW is one application, each COLUMN is one question.`],
      ['2.', 'Cells that already have a value are what we hold today — correct them if they are wrong.'],
      ['3.', 'Leave a cell BLANK if you do not know the answer. A blank never erases what we already have.'],
      ['4.', `Where a question has a fixed set of answers, the options are listed on the "${SHEET_VALUES}" sheet.`],
      ['5.', 'Yes/No questions accept Yes or No.'],
      ['6.', 'Save the file and email it back. Nothing is changed until a reviewer approves each edit.'],
      [],
      ['Please do NOT'],
      ['', 'Rename, reorder or delete columns.'],
      ['', 'Rename or delete any sheet — including the hidden "_meta" sheet.'],
      ['', 'Change the Application column: it identifies the row and is not imported as an answer.'],
      ['', 'Paste rows from another workbook (add a note below instead).'],
      [],
      ['Notes from the person who sent you this form'],
      ['', note || '(none)'],
    ];
    const wsReadme = XLSX.utils.aoa_to_sheet(readme);
    wsReadme['!cols'] = [{ wch: 14 }, { wch: 110 }];
    XLSX.utils.book_append_sheet(wb, wsReadme, SHEET_README);

    // --- Applications (the form itself) ------------------------------------
    const header = columns.map((c) => c.label + (c.mandatory && c.key !== 'name' ? ' *' : ''));
    const aoa = [header];
    chosen.forEach((a) => {
      aoa.push(columns.map((c) => (c.key === 'name'
        ? (a.name || (a.fields || {}).name || '')
        : fmt((a.fields || {})[c.key]))));
    });
    const wsData = XLSX.utils.aoa_to_sheet(aoa);
    wsData['!cols'] = columns.map((c, i) => ({ wch: i === 0 ? 34 : Math.min(46, Math.max(16, c.label.length + 6)) }));
    wsData['!freeze'] = { xSplit: 1, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, wsData, SHEET_DATA);

    // --- Valid values ------------------------------------------------------
    // SheetJS cannot reliably write real data-validation dropdowns, so we ship
    // the allowed options as a lookup sheet and validate on the way back in.
    const valAoa = [['Question', 'Allowed value']];
    columns.forEach((c) => {
      allowedValues(c).forEach((v) => valAoa.push([c.label, v]));
    });
    if (valAoa.length === 1) valAoa.push(['(no fixed-answer questions in this form)', '']);
    const wsVals = XLSX.utils.aoa_to_sheet(valAoa);
    wsVals['!cols'] = [{ wch: 46 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsVals, SHEET_VALUES);

    // --- _meta (the round-trip identity map) -------------------------------
    const metaAoa = [
      ['kind', 'index', 'key', 'label'],
      ['form', '', 'tenantId', tenantId],
      ['form', '', 'generatedAt', new Date().toISOString()],
      ['form', '', 'dataSheet', SHEET_DATA],
    ];
    columns.forEach((c, i) => metaAoa.push(['column', i, c.key, c.label]));
    chosen.forEach((a, i) => metaAoa.push(['row', i, a.id, a.name || '']));
    const wsMeta = XLSX.utils.aoa_to_sheet(metaAoa);
    wsMeta['!cols'] = [{ wch: 10 }, { wch: 8 }, { wch: 28 }, { wch: 46 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, SHEET_META);

    // Hide _meta so the owner is not tempted to "tidy it up".
    wb.Workbook = wb.Workbook || {};
    wb.Workbook.Sheets = wb.SheetNames.map((n) => ({ name: n, Hidden: n === SHEET_META ? 1 : 0 }));

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `primordia-discovery-form-${norm(tenantId) || 'tenant'}-${stamp}.xlsx`);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Search applications"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <Button size="small" onClick={selectAll}>All ({visible.length})</Button>
        <Button size="small" onClick={selectIncomplete}>Only incomplete</Button>
        <Button size="small" onClick={selectNone}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Chip variant="outlined" label={`workspace: ${tenantId}`} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ flex: 1, maxHeight: 380, overflow: 'auto' }}>
          <Typography sx={{ p: 1.5, pb: 0.5, fontWeight: 700, color: HEAD }}>
            Applications to include ({selectedApps.size} selected)
          </Typography>
          {loading && <LinearProgress />}
          <List dense disablePadding>
            {shown.map((a) => (
              <ListItemButton key={a.id} onClick={() => toggleApp(a.id)} dense>
                <Checkbox edge="start" size="small" checked={selectedApps.has(a.id)} tabIndex={-1} disableRipple />
                <ListItemText
                  primary={a.name}
                  secondary={`${a.completion || 0}% complete`}
                  primaryTypographyProps={{ fontSize: 14 }}
                  secondaryTypographyProps={{ fontSize: 12, color: GREY }}
                />
              </ListItemButton>
            ))}
            {!loading && !visible.length && (
              <Typography sx={{ p: 2, color: GREY }} variant="body2">No applications match.</Typography>
            )}
          </List>
          {visible.length > shown.length && (
            <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: '#5C6B7A' }}>
              Showing the first {shown.length.toLocaleString()} of {visible.length.toLocaleString()} matches — search to
              narrow. <b>Select all</b> still selects all {visible.length.toLocaleString()}.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, maxHeight: 380, overflow: 'auto' }}>
          <Typography sx={{ p: 1.5, pb: 0.5, fontWeight: 700, color: HEAD }}>
            Sections to ask about ({bodyFields.length} questions)
          </Typography>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {sections.map((s) => {
              const count = fields.filter((f) => f.section === s && f.key !== 'name').length;
              return (
                <FormControlLabel
                  key={s}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedSections.has(s)}
                      onChange={() => toggleSection(s)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{s} <span style={{ color: GREY }}>({count})</span></span>}
                />
              );
            })}
          </Box>
        </Paper>
      </Stack>

      <TextField
        size="small"
        label="Note to the owner (optional — appears on the Read me sheet)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        multiline
        minRows={2}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!canGenerate}
          onClick={generate}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
        >
          Generate .xlsx ({chosen.length} apps × {bodyFields.length} questions)
        </Button>
        {!canGenerate && (
          <Typography variant="body2" sx={{ color: GREY }}>
            Pick at least one application and one section.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// TAB 2 — Ingest
// ---------------------------------------------------------------------------
function IngestTab({ tenantId, apps, fields, fieldByKey, onApplied }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [err, setErr] = useState('');
  const [usedMeta, setUsedMeta] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const [changes, setChanges] = useState([]);          // flat list of proposed edits
  const [accepted, setAccepted] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);

  const appsById = useMemo(() => {
    const m = new Map();
    apps.forEach((a) => m.set(a.id, a));
    return m;
  }, [apps]);
  const appsByName = useMemo(() => {
    const m = new Map();
    apps.forEach((a) => m.set(norm(a.name), a));
    return m;
  }, [apps]);

  const reset = () => {
    setFileName(''); setErr(''); setUsedMeta(true); setUnmatched([]);
    setChanges([]); setAccepted(new Set()); setProgress(0); setSummary(null);
  };

  // Column label -> field key, for the degraded path (no _meta sheet).
  const mapHeaderToKey = useCallback((label) => {
    const n = norm(String(label).replace(/\*$/, ''));
    if (!n) return '';
    const byLabel = fields.find((f) => norm(f.label) === n);
    if (byLabel) return byLabel.key;
    const byKey = fields.find((f) => norm(f.key) === n);
    if (byKey) return byKey.key;
    const alias = Object.keys(ALIASES).find((k) => ALIASES[k].indexOf(n) >= 0);
    return alias || '';
  }, [fields]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      buildPreview(wb);
    } catch (e2) {
      setErr('Could not read that file. Expected the .xlsx generated by this screen (or any .xlsx/.csv with an Applications sheet).');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const buildPreview = (wb) => {
    // ---- recover the identity map ----------------------------------------
    const metaWs = wb.Sheets[SHEET_META];
    const metaColKeyByIndex = new Map();
    const metaKeyByLabel = new Map();
    const metaIdByRowIndex = new Map();
    const metaIdByName = new Map();
    let dataSheetName = SHEET_DATA;
    let hasMeta = false;

    if (metaWs) {
      const grid = XLSX.utils.sheet_to_json(metaWs, { header: 1, defval: '' });
      grid.slice(1).forEach((r) => {
        const kind = String(r[0] || '').toLowerCase();
        const idx = Number(r[1]);
        const key = String(r[2] || '');
        const label = String(r[3] || '');
        if (kind === 'column' && Number.isFinite(idx) && key) {
          metaColKeyByIndex.set(idx, key);
          metaKeyByLabel.set(norm(label), key);
        } else if (kind === 'row' && key) {
          if (Number.isFinite(idx)) metaIdByRowIndex.set(idx, key);
          if (label) metaIdByName.set(norm(label), key);
        } else if (kind === 'form' && key === 'dataSheet' && label) {
          dataSheetName = label;
        }
      });
      hasMeta = metaColKeyByIndex.size > 0;
    }
    setUsedMeta(hasMeta);

    const ws = wb.Sheets[dataSheetName] || wb.Sheets[SHEET_DATA]
      || wb.Sheets[wb.SheetNames.filter((n) => n !== SHEET_META && n !== SHEET_README && n !== SHEET_VALUES)[0]]
      || wb.Sheets[wb.SheetNames[0]];
    if (!ws) { setErr(`No "${SHEET_DATA}" sheet found in that workbook.`); return; }

    const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (!grid.length) { setErr('That sheet is empty.'); return; }
    const header = (grid[0] || []).map((h) => String(h).trim());
    const dataRows = grid.slice(1).filter((r) => (r || []).some((c) => String(c).trim()));

    // ---- columns ----------------------------------------------------------
    // Prefer the _meta column at this index, but only when its label still
    // matches the header — otherwise the columns were reordered, so fall back
    // to the label, then to alias matching.
    const colKeys = header.map((label, i) => {
      if (hasMeta) {
        const byIdx = metaColKeyByIndex.get(i);
        const f = byIdx ? fieldByKey.get(byIdx) : null;
        if (f && norm(f.label) === norm(String(label).replace(/\*$/, ''))) return byIdx;
        const byLabel = metaKeyByLabel.get(norm(String(label).replace(/\*$/, '')));
        if (byLabel) return byLabel;
      }
      return mapHeaderToKey(label);
    });
    const nameCol = colKeys.indexOf('name') >= 0 ? colKeys.indexOf('name') : 0;

    // ---- rows -------------------------------------------------------------
    const out = [];
    const misses = [];
    dataRows.forEach((row, i) => {
      const rowName = String(row[nameCol] || '').trim();
      let app = null;
      if (hasMeta) {
        const id = metaIdByName.get(norm(rowName)) || metaIdByRowIndex.get(i);
        if (id) app = appsById.get(id) || null;
      }
      if (!app) app = appsByName.get(norm(rowName)) || null;
      if (!app) { if (rowName) misses.push(rowName); return; }

      colKeys.forEach((key, c) => {
        if (!key || key === 'name' || c === nameCol) return;
        const field = fieldByKey.get(key);
        if (!field) return;
        const raw = row[c];
        if (String(raw === undefined || raw === null ? '' : raw).trim() === '') return; // blank never erases
        const value = parseCell(raw, field);
        const oldVal = (app.fields || {})[key];
        if (fmt(oldVal).trim().toLowerCase() === fmt(value).trim().toLowerCase()) return;
        out.push({
          rowId: `${app.id}::${key}`,
          appId: app.id,
          appName: app.name,
          key,
          label: field.label,
          section: field.section,
          oldText: fmt(oldVal),
          newText: fmt(value),
          value,
          warning: violatesPicklist(field, value)
            ? `Not one of the allowed values: ${allowedValues(field).join(', ')}`
            : '',
        });
      });
    });

    setChanges(out);
    setUnmatched([...new Set(misses)]);
    // Clean edits are pre-accepted; anything that breaks a picklist has to be
    // ticked deliberately, so a typo never lands silently on a fact sheet.
    setAccepted(new Set(out.filter((c) => !c.warning).map((c) => c.rowId)));
  };

  const grouped = useMemo(() => {
    const m = new Map();
    changes.forEach((c) => {
      if (!m.has(c.appId)) m.set(c.appId, { appId: c.appId, appName: c.appName, rows: [] });
      m.get(c.appId).rows.push(c);
    });
    return [...m.values()].sort((a, b) => a.appName.localeCompare(b.appName));
  }, [changes]);

  const toggleRow = (rowId) => {
    const next = new Set(accepted);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    setAccepted(next);
  };
  const toggleGroup = (g, on) => {
    const next = new Set(accepted);
    g.rows.forEach((r) => { if (on) next.add(r.rowId); else next.delete(r.rowId); });
    setAccepted(next);
  };

  const warnCount = changes.filter((c) => c.warning).length;

  const apply = async () => {
    setBusy(true); setProgress(0); setErr('');
    const groups = grouped
      .map((g) => ({ ...g, rows: g.rows.filter((r) => accepted.has(r.rowId)) }))
      .filter((g) => g.rows.length);
    let updated = 0, errored = 0, fieldsWritten = 0;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      try {
        const fs = (await store.getFactSheet(g.appId)) || appsById.get(g.appId);
        if (!fs) throw new Error('fact sheet not found');
        const patch = {};
        g.rows.forEach((r) => { patch[r.key] = r.value; });
        await store.saveFactSheet({ ...fs, fields: { ...(fs.fields || {}), ...patch } });
        updated += 1;
        fieldsWritten += g.rows.length;
      } catch (e) {
        errored += 1;
      }
      setProgress(Math.round(((i + 1) / groups.length) * 100));
    }
    setBusy(false);
    setSummary({
      updated,
      errored,
      fieldsWritten,
      skipped: changes.length - accepted.size,
      unmatched: unmatched.length,
    });
    if (onApplied) onApplied();
  };

  return (
    <Stack spacing={2}>
      {err && <Alert severity="error">{err}</Alert>}

      {summary && (
        <Alert severity={summary.errored ? 'warning' : 'success'}>
          Applied to <b>{tenantId}</b>: {summary.updated} applications updated
          ({summary.fieldsWritten} field values written), {summary.skipped} edits skipped,
          {' '}{summary.errored} errored{summary.unmatched ? `, ${summary.unmatched} rows unmatched` : ''}.
        </Alert>
      )}

      {!changes.length && !summary ? (
        <Box
          onClick={() => fileRef.current && fileRef.current.click()}
          sx={{
            border: '2px dashed #cfd8dc', borderRadius: 2, p: 5, textAlign: 'center',
            cursor: 'pointer', '&:hover': { borderColor: BLUE, bgcolor: '#f7fbff' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />
          <Typography sx={{ fontWeight: 600, mt: 1 }}>Choose the completed form</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            .xlsx returned by an application owner — nothing is written until you approve the diff
          </Typography>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip label={fileName || 'workbook'} />
            <Chip color="primary" label={`${changes.length} proposed changes`} />
            <Chip label={`${grouped.length} applications`} />
            {!!warnCount && <Chip color="warning" label={`${warnCount} value warnings`} />}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Start over with another file">
              <span>
                <IconButton size="small" onClick={reset} disabled={busy}><RestartAltIcon /></IconButton>
              </span>
            </Tooltip>
          </Stack>

          {!usedMeta && (
            <Alert severity="warning">
              This workbook has no <b>_meta</b> sheet, so it was not the file we generated (or it was rebuilt).
              Rows were matched by <b>application name</b> and columns by <b>column heading</b>, which is a best
              effort — check the diff carefully before applying.
            </Alert>
          )}

          {!!unmatched.length && (
            <Alert severity="info">
              {unmatched.length} row{unmatched.length === 1 ? '' : 's'} did not match any application in this
              workspace and were ignored: {unmatched.slice(0, 8).join(', ')}
              {unmatched.length > 8 ? ' …' : ''}
            </Alert>
          )}

          {!changes.length && !summary && (
            <Alert severity="info">No differences — every answer in the form matches what we already hold.</Alert>
          )}

          {grouped.map((g) => {
            const on = g.rows.filter((r) => accepted.has(r.rowId)).length;
            return (
              <Paper key={g.appId} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    size="small"
                    checked={on === g.rows.length}
                    indeterminate={on > 0 && on < g.rows.length}
                    onChange={(e) => toggleGroup(g, e.target.checked)}
                    disabled={busy}
                  />
                  <Typography sx={{ fontWeight: 700, color: NAVY }}>{g.appName}</Typography>
                  <Typography variant="body2" sx={{ color: GREY }}>
                    {on}/{g.rows.length} changes accepted
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell padding="checkbox" />
                      <TableCell>Field</TableCell>
                      <TableCell>Current</TableCell>
                      <TableCell>From the form</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.rows.map((r) => (
                      <TableRow key={r.rowId} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={accepted.has(r.rowId)}
                            onChange={() => toggleRow(r.rowId)}
                            disabled={busy}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.label}
                          <Typography variant="caption" sx={{ display: 'block', color: GREY }}>{r.section}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: GREY, maxWidth: 260 }}>{r.oldText || <em>empty</em>}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Box sx={{ color: NAVY, fontWeight: 600 }}>{r.newText}</Box>
                          {!!r.warning && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                              <WarningAmberIcon fontSize="inherit" sx={{ color: '#B26A00' }} />
                              <Typography variant="caption" sx={{ color: '#B26A00' }}>{r.warning}</Typography>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            );
          })}

          {busy && <LinearProgress variant="determinate" value={progress} />}

          {!!changes.length && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                disabled={busy || !accepted.size}
                onClick={apply}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
              >
                {busy ? `Applying ${progress}%` : `Apply ${accepted.size} accepted change${accepted.size === 1 ? '' : 's'}`}
              </Button>
              <Typography variant="body2" sx={{ color: GREY }}>
                Unchecked rows are left exactly as they are today.
              </Typography>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
